/**
 * Centralized Purchase Thresholds Configuration
 * All price thresholds and strategy parameters for purchasing decisions
 */

export const PURCHASE_THRESHOLDS = {
  // Basic price categories (DKK/MWh)
  PRICE_CATEGORIES: {
    NEGATIVE: 0,           // Always buy at negative prices
    VERY_CHEAP: 75,        // Excellent prices - aggressive buying
    EXCELLENT: 100,        // Very good prices - buy aggressively
    CHEAP: 150,            // Good prices - buy actively
    NORMAL: 200,           // Normal prices - selective buying
    ELEVATED: 300,         // Higher prices - conservative buying
    HIGH: 400,             // High prices - minimal buying
    VERY_HIGH: 500,        // Very high prices - emergency only
    EXTREME: 600,          // Extreme prices - critical emergency only
  },

  // Grid Purchase Strategy
  GRID_PURCHASE: {
    // Maximum prices for different situations
    MAX_UNMET_DEMAND: 400,      // Never buy above this to meet demand
    MAX_MIN_LEVEL: 350,          // Max price to maintain minimum storage
    MAX_TARGET_LEVEL: 200,       // Max price to fill to target level
    MAX_HTES_FILL: 150,          // Max price to fill HTES
    MAX_NIGHT_CHARGE: 150,       // Max price for night charging
    MAX_FORECAST_DRIVEN: 300,    // Max price for forecast-driven purchases

    // Emergency limits
    MAX_MODERATE_EMERGENCY: 450, // Max price at moderate emergency (reduce to 10 MW)
    MAX_CRITICAL_EMERGENCY: 600, // Max price at critical emergency (reduce to 5 MW)

    // Amount limits at high prices
    LIMIT_AT_300: 15,            // Max MW to buy at >300 DKK
    LIMIT_AT_450: 10,            // Max MW to buy at >450 DKK
    LIMIT_AT_600: 5,             // Max MW to buy at >600 DKK
  },

  // HTES Charging Strategy
  HTES_CHARGING: {
    // Aggressive charging thresholds
    FILL_TO_99_BELOW: 100,       // Fill to 99% below this price
    FILL_TO_95_BELOW: 150,       // Fill to 95% below this price
    FILL_TO_80_BELOW: 200,       // Fill to 80% below this price
    FILL_TO_70_BELOW: 250,       // Fill to 70% below this price

    // Target levels by price
    TARGET_99: 0.99,
    TARGET_95: 0.95,
    TARGET_80: 0.80,
    TARGET_70: 0.70,
  },

  // Turbine Operation Strategy
  TURBINE_OPERATION: {
    // Minimum prices for turbine discharge
    MIN_WASTE_ACCEPTANCE: 50,    // Min price to run turbine for waste heat
    MIN_SPACE_CREATION: 200,     // Min price to run turbine for space
    MIN_ECONOMICAL: 100,         // Min price for HTES discharge to be economical
    MIN_SOLAR_SPACE: 200,        // Min price to make space for solar

    // Revenue thresholds
    MIN_SOLAR_VALUE_RATIO: 0.3,  // Min ratio of electricity revenue to solar value
  },

  // Storage Level Thresholds
  STORAGE_LEVELS: {
    // LTES levels
    LTES_CRITICAL: 0.10,         // Critical emergency level
    LTES_MIN: 0.25,              // Minimum operational level
    LTES_TARGET: 0.45,           // Target level for balanced operation
    LTES_HIGH: 0.90,             // High level - may need space
    LTES_VERY_HIGH: 0.95,        // Very high - definitely need space

    // HTES levels
    HTES_MIN: 0.05,              // Minimum operational level
    HTES_LOW: 0.15,              // Low level - may affect turbine
    HTES_MIN_TURBINE: 0.20,      // Minimum for turbine operation
    HTES_TARGET: 0.70,           // Target level for balanced operation
    HTES_HIGH: 0.80,             // High level - ready for arbitrage
  },

  // Forecast-Based Strategy
  FORECAST: {
    // Urgency thresholds for forecast-driven purchases
    MIN_URGENCY: 0.7,            // Minimum forecast urgency to trigger purchase

    // Price comparison ratios
    VALLEY_TOLERANCE: 1.05,      // Buy if within 5% of valley price
    AVG_DISCOUNT: 0.85,          // Buy if 15% below average

    // Forecast windows (hours)
    SHORT_WINDOW: 6,             // Short-term forecast window
    MEDIUM_WINDOW: 12,           // Medium-term forecast window
    LONG_WINDOW: 24,             // Long-term forecast window (now extended to 48h)

    // Predicted storage thresholds
    LOW_PREDICTED_STORAGE: 20,   // MWh - trigger purchase if predicted low

    // Peak preparation
    PEAK_THRESHOLD: 500,         // If price above this coming, prepare
    PREP_MAX_PRICE: 200,         // Max price to pay for preparation
  },

  // Time-Based Strategy
  TIME_WINDOWS: {
    // Night charging (typically cheaper)
    NIGHT_START: 22,             // Hour to start night charging
    NIGHT_END: 6,                // Hour to end night charging

    // Seasonal adjustments
    WINTER_MONTHS: [11, 12, 1, 2], // November - February
    SUMMER_MONTHS: [5, 6, 7, 8],   // May - August
  },
};

/**
 * Get dynamic thresholds based on scenario configuration
 * @param {Object} scenarioConfig - The active scenario configuration
 * @returns {Object} Adjusted thresholds for the scenario
 */
export function getScenarioThresholds(scenarioConfig) {
  const thresholds = { ...PURCHASE_THRESHOLDS };

  // Adjust thresholds based on equipment capacity
  if (scenarioConfig) {
    const { heater_MW = 30, battery_MW = 3, htes_modules = 2 } = scenarioConfig;

    // Smaller heaters = more conservative on high prices
    if (heater_MW < 20) {
      thresholds.GRID_PURCHASE.MAX_UNMET_DEMAND = 350;
      thresholds.GRID_PURCHASE.MAX_FORECAST_DRIVEN = 250;
    }

    // Larger HTES capacity = more aggressive charging
    if (htes_modules >= 3) {
      thresholds.HTES_CHARGING.FILL_TO_99_BELOW = 120;
      thresholds.HTES_CHARGING.FILL_TO_95_BELOW = 170;
    }

    // With battery = more arbitrage opportunities
    if (battery_MW >= 5) {
      thresholds.FORECAST.MIN_URGENCY = 0.6; // More responsive to forecast
    }
  }

  return thresholds;
}

/**
 * Check if price is in a specific category
 * @param {number} price - Spot price in DKK/MWh
 * @param {string} category - Category name (e.g., 'VERY_CHEAP', 'HIGH')
 * @returns {boolean}
 */
export function isPriceInCategory(price, category) {
  const cats = PURCHASE_THRESHOLDS.PRICE_CATEGORIES;

  switch(category) {
    case 'NEGATIVE': return price < cats.NEGATIVE;
    case 'VERY_CHEAP': return price >= cats.NEGATIVE && price < cats.VERY_CHEAP;
    case 'EXCELLENT': return price >= cats.VERY_CHEAP && price < cats.EXCELLENT;
    case 'CHEAP': return price >= cats.EXCELLENT && price < cats.CHEAP;
    case 'NORMAL': return price >= cats.CHEAP && price < cats.NORMAL;
    case 'ELEVATED': return price >= cats.NORMAL && price < cats.ELEVATED;
    case 'HIGH': return price >= cats.ELEVATED && price < cats.HIGH;
    case 'VERY_HIGH': return price >= cats.HIGH && price < cats.VERY_HIGH;
    case 'EXTREME': return price >= cats.VERY_HIGH;
    default: return false;
  }
}

export default PURCHASE_THRESHOLDS;
